import { useState, useMemo, useEffect, useCallback } from 'react';
import { ArrowUpDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import {
  VariableSelectionDropdown,
  type VariableOption,
} from '@/components/maps/insets/VariableSelectionDropdown';
import type { MapTableInsetRow } from '@/components/maps/insets';

interface AtAGlanceTileProps {
  data: MapTableInsetRow[];
  aggregationRow?: MapTableInsetRow | null;
  availableVariables?: VariableOption[];
  selectedVariables?: string[];
  onSelectedVariablesChange?: (variables: string[]) => void;
  formatVariableName?: (slug: string) => string;
  csvMetadata?: {
    weatherModel?: string;
    validDate?: Date | null;
    modelRunDate?: Date | null;
    predictionType?: string;
  };
  loading?: boolean;
  areaNameKey?: string;
  title?: string;
  maxVisibleColumns?: number;
  maxColumnWidth?: number;
}

const PAGE_SIZE = 10;

export function AtAGlanceTile({
  data,
  aggregationRow,
  availableVariables = [],
  selectedVariables,
  onSelectedVariablesChange,
  formatVariableName,
  csvMetadata,
  loading = false,
  areaNameKey = 'rowName',
  title = 'Forecast Overview',
  maxVisibleColumns = 5,
  maxColumnWidth = 200,
}: AtAGlanceTileProps) {
  const [filter, setFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
    setFilter('');
  }, [data]);

  const visibleData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((row) => {
      const flatRow: Record<string, any> = {
        id: row.rowId,
        name: row.rowName,
        slug: row.rowSlug,
        rowName: row.rowName,
      };
      row.rowData?.forEach((varData) => {
        if (varData.variableId) {
          let value = varData.variableValue;
          if (typeof value === 'string') {
            const parsed = parseFloat(value);
            value = isNaN(parsed) ? value : parsed;
          }
          flatRow[varData.variableId] = value;
          if (varData.variableSlug) flatRow[varData.variableSlug] = value;
        }
      });
      return flatRow;
    });
  }, [data]);

  const columnKeys = useMemo(() => {
    if (!data || data.length === 0) return [areaNameKey];
    const variablesMap = new Map<string, { id: string; name: string; order: number | null }>();
    data.forEach((row) => {
      row.rowData?.forEach((varData) => {
        if (varData.variableId && !variablesMap.has(varData.variableId)) {
          variablesMap.set(varData.variableId, {
            id: varData.variableId,
            name: varData.variableName,
            order: varData.variableOrder ?? null,
          });
        }
      });
    });
    const sortedVariables = Array.from(variablesMap.values()).sort((a, b) => {
      if (a.order !== null && b.order !== null) return a.order - b.order;
      if (a.order !== null) return -1;
      if (b.order !== null) return 1;
      return a.name.localeCompare(b.name);
    });
    if (selectedVariables && selectedVariables.length > 0) {
      const byId = new Map(sortedVariables.map((v) => [v.id, v]));
      const orderedSelected = selectedVariables.filter((id) => byId.has(id));
      return [areaNameKey, ...orderedSelected];
    }
    return [areaNameKey, ...sortedVariables.map((v) => v.id).slice(0, maxVisibleColumns - 1)];
  }, [data, areaNameKey, selectedVariables, maxVisibleColumns]);

  const variableNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    data?.forEach((row) => {
      row.rowData?.forEach((varData) => {
        if (varData.variableId && varData.variableName && !map.has(varData.variableId)) {
          map.set(varData.variableId, varData.variableName);
        }
      });
    });
    return map;
  }, [data]);

  const processedData = useMemo(() => {
    let result = [...visibleData];
    if (filter) {
      result = result.filter((row) =>
        row.name?.toLowerCase().includes(filter.toLowerCase())
      );
    }
    if (sortColumn) {
      result.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return sortDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }
    return result;
  }, [visibleData, filter, sortColumn, sortDirection]);

  const paginatedData = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return processedData.slice(start, start + PAGE_SIZE);
  }, [processedData, pageIndex]);

  const pageCount = Math.ceil(processedData.length / PAGE_SIZE);

  const aggregationRowData = useMemo(() => {
    if (!aggregationRow) return null;
    const flatRow: Record<string, any> = {
      id: aggregationRow.rowId,
      name: aggregationRow.rowName,
      slug: aggregationRow.rowSlug,
      rowName: aggregationRow.rowName,
    };
    aggregationRow.rowData?.forEach((varData) => {
      if (varData.variableId) {
        let value = varData.variableValue;
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          value = isNaN(parsed) ? value : parsed;
        }
        flatRow[varData.variableId] = value;
      }
    });
    return flatRow;
  }, [aggregationRow]);

  const formatCellValue = useCallback((value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  }, []);

  const getColumnLabel = useCallback(
    (key: string): string => {
      if (key === areaNameKey) return 'Area';
      if (variableNamesMap.has(key)) return variableNamesMap.get(key)!;
      return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },
    [areaNameKey, variableNamesMap]
  );

  const handleSort = useCallback(
    (key: string) => {
      setSortDirection((prev) => (sortColumn === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
      setSortColumn(key);
    },
    [sortColumn]
  );

  const escapeCSVValue = useCallback((value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!processedData || processedData.length === 0) return;
    const csvLines: string[] = [];
    csvLines.push('# Report Information');
    csvLines.push(`Report Title,${escapeCSVValue(title)}`);
    if (csvMetadata?.weatherModel) csvLines.push(`Weather Model,${escapeCSVValue(csvMetadata.weatherModel)}`);
    if (csvMetadata?.validDate) {
      csvLines.push(`Valid For,${escapeCSVValue(csvMetadata.validDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }))}`);
    }
    if (csvMetadata?.modelRunDate) {
      const runStr = `${csvMetadata.modelRunDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} ${csvMetadata.modelRunDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} Model Run`;
      csvLines.push(`Model Run,${escapeCSVValue(runStr)}`);
    }
    if (csvMetadata?.predictionType) csvLines.push(`Prediction Type,${escapeCSVValue(csvMetadata.predictionType)}`);
    const variableNames = columnKeys
      .filter((key) => key !== areaNameKey)
      .map(getColumnLabel);
    if (variableNames.length > 0) {
      csvLines.push(`Variables Included,${escapeCSVValue(variableNames.join('; '))}`);
    }
    csvLines.push(`Generated At,${escapeCSVValue(new Date().toLocaleString())}`);
    csvLines.push('');
    csvLines.push(columnKeys.map((key) => escapeCSVValue(getColumnLabel(key))).join(','));
    processedData.forEach((row) => {
      csvLines.push(columnKeys.map((key) => escapeCSVValue(formatCellValue(row[key]))).join(','));
    });
    if (aggregationRowData) {
      csvLines.push(
        columnKeys.map((key, idx) => {
          if (idx === 0) return escapeCSVValue(aggregationRowData.name ? `${aggregationRowData.name} (Total)` : 'Total');
          const value = aggregationRowData[key];
          return escapeCSVValue(value !== undefined && value !== null ? formatCellValue(value) : '-');
        }).join(',')
      );
    }
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [processedData, columnKeys, areaNameKey, getColumnLabel, formatCellValue, aggregationRowData, title, csvMetadata, escapeCSVValue]);

  if (!visibleData.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-xs text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden relative">
      {loading && (
        <div className="absolute inset-0 z-20 bg-background/50 backdrop-blur-[0.5px]" aria-hidden />
      )}
      {/* Controls */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 flex-shrink-0">
        <Input
          className="flex-1 max-w-[180px] text-xs h-7"
          placeholder="Filter..."
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPageIndex(0); }}
        />
        {availableVariables.length > 0 && onSelectedVariablesChange && (
          <VariableSelectionDropdown
            availableVariables={availableVariables}
            selectedVariables={selectedVariables || []}
            onSelectedVariablesChange={onSelectedVariablesChange}
            formatVariableName={formatVariableName}
            label="Columns"
          />
        )}
        <Button variant="ghost" size="sm" className="text-xs h-7 flex-shrink-0" onClick={handleExportCSV} title="Download as CSV">
          <Download className="h-4 w-4" />
        </Button>
      </div>

      {/* Table */}
      <div className="border-x border-t relative flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div style={{ width: 'max-content', minWidth: '100%' }}>
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
                <TableRow>
                  {columnKeys.map((key) => (
                    <TableHead key={key} className="p-1">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort(key)}
                        className="p-0 h-auto font-medium text-xs flex items-center gap-1 w-full justify-start"
                      >
                        <span className="break-words whitespace-normal text-left" style={{ maxWidth: '94px' }}>
                          {getColumnLabel(key)}
                        </span>
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 mt-0.5" />
                      </Button>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <TableRow key={row.id || idx}>
                      {columnKeys.map((key) => (
                        <TableCell key={key} className="p-2 text-xs">
                          <div className="break-words whitespace-normal overflow-hidden" style={{ maxWidth: `${maxColumnWidth}px` }}>
                            {formatCellValue(row[key])}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columnKeys.length} className="h-24 text-center text-xs">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {paginatedData.length > 0 && aggregationRowData && (
                <TableFooter className="bg-muted border-t text-secondary-foreground">
                  <TableRow>
                    {columnKeys.map((key, index) => (
                      <TableCell key={key} className="p-2 text-xs font-medium">
                        {index === 0
                          ? aggregationRowData.name ? `${aggregationRowData.name} (Total)` : 'Total'
                          : formatCellValue(aggregationRowData[key] ?? null)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-3 py-1 flex-shrink-0 bg-background border-x border-b">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))} disabled={pageIndex === 0}>
            Previous
          </Button>
          <span className="text-xs">Page {pageIndex + 1} of {pageCount || 1}</span>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPageIndex((prev) => Math.min(pageCount - 1, prev + 1))} disabled={pageIndex >= pageCount - 1}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
